import React, {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '@components/modal/modal.component';
import {StyledFooter} from '@components/pane/pane.style';
import {StyledModalContent} from '@components/modal/modal.style';
import {StyledButton,} from "@components/button/button.style";
import {disableSwipeToClose} from "@utils/tools/modals";
import {Notification} from "@models/notification";
import PreviewModal from "@app/mainPage/tabs/notifications/modals/previewModal.component";
import {getNotAcceptedObligatoryNotifications, hasUnreadNotifications, markNotificationAsRead} from "@services/push.service";
import {IonLoading} from "@ionic/react";
import {getButtonAction, getButtonDescription} from "@services/notification.service";
import {NotificationAction} from "@enums/notificationAction";
import {Browser} from "@capacitor/browser";
import {useHistory} from "react-router-dom";
import {MainPageContext} from "@app/mainPage/mainPage.component";

type NotAcceptedObligatoryNotificationsModalProps = {
    isOpen: boolean,
    onClose: () => void,
    onSuccess: () => void,
};

const NotAcceptedObligatoryNotificationsModal: React.FC<NotAcceptedObligatoryNotificationsModalProps> = ({isOpen, onClose, onSuccess}: NotAcceptedObligatoryNotificationsModalProps) => {
    const {t} = useTranslation();
    const history = useHistory();
    const mainPageManager = useContext(MainPageContext);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showNotificationPreviewModal, setShowNotificationPreviewModal] = useState<boolean>(false);
    const [selectedNotification, setSelectedNotification] = useState<Notification | undefined>(undefined);

    const acceptedNotificationsCount = useRef<number>(0);

    useEffect(() => {
        if (isOpen) {
            fetchNotAcceptedObligatoryNotifications();
        }
    }, [isOpen]);

    const fetchNotAcceptedObligatoryNotifications = async (afterAccept: boolean = false) => {
        setIsLoading(true);

        let notifications: Notification[] = [];
        try {
            notifications = await getNotAcceptedObligatoryNotifications();
        } catch (error) {
            console.error(error);
        }

        if (notifications.length > 0) {
            setSelectedNotification(notifications.reduce((min, n) => n.id < min.id ? n : min))

            if (afterAccept) {
                acceptedNotificationsCount.current++;
            }
        } else {
            onSuccess();
        }

        setIsLoading(false);
    }

    const gotoAction = async (notification: Notification) => {
        if (notification.status === 'sent') {
            await markNotificationAsRead(notification.id);
        }
        notification.status = 'read';

        let unread = await hasUnreadNotifications();
        mainPageManager.setNotificationBadge(unread);

        if (notification.notification.redirect_to === NotificationAction.URL) {
            await Browser.open({ url: notification.notification.redirect_to_parameter });
        } else {
            history.push(getButtonAction(notification));
        }

        setSelectedNotification(undefined);
        onClose();
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                canDismiss={disableSwipeToClose}
                disableClose={true}
                onDidDismiss={() => onClose()}
            >
                <StyledModalContent>
                    <h1 className="title">{t("notAcceptedObligatoryNotifications.title")}</h1>
                    <div className="content">
                        <p>
                            {t("notAcceptedObligatoryNotifications.info")}
                        </p>
                    </div>
                    <StyledFooter className="modal footer no-shadow">
                        <StyledButton onClick={() => setShowNotificationPreviewModal(true)}>
                            {
                                acceptedNotificationsCount.current >= 1
                                    ? t("notAcceptedObligatoryNotifications.showNextNotification")
                                    : t("notAcceptedObligatoryNotifications.showNotification")
                            }
                        </StyledButton>
                    </StyledFooter>
                </StyledModalContent>
            </Modal>
            {
                selectedNotification &&
                <PreviewModal
                    isOpen={showNotificationPreviewModal}
                    onClose={() => setShowNotificationPreviewModal(false)}
                    notification={selectedNotification}
                    buttonText={t(getButtonDescription(selectedNotification))}
                    onAction={() => gotoAction(selectedNotification)}
                    onObligatoryAccept={async () => {
                        setShowNotificationPreviewModal(false);
                        await fetchNotAcceptedObligatoryNotifications(true);
                    }}
                />
            }
            <IonLoading isOpen={isLoading}/>
        </>
    );
};

export default NotAcceptedObligatoryNotificationsModal;